import React, { useEffect, useRef } from 'react';
import { useAddUrlToAirtable, getStoredUrls } from '../../hooks/useAddUrlToAirtable';
import { logger } from '../../utils/logger';

interface SendToAirtableProviderProps {
  children: React.ReactNode;
  // How often to check for stored URLs (in milliseconds)
  checkInterval?: number;
  // Minimum number of URLs to collect before sending
  minUrls?: number;
}

/**
 * Provider component that periodically checks for stored URLs and sends them to Airtable
 * This component should be mounted high in the component tree, e.g. in App.tsx
 */
export const SendToAirtableProvider: React.FC<SendToAirtableProviderProps> = ({
  children,
  checkInterval = 60000, // Default: check every minute
  minUrls = 1, // Default: send when we have at least 5 URLs
}) => {
  const { sendStoredUrlsToAirtable, loading } = useAddUrlToAirtable();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Function to check for stored URLs and send them if there are enough
  const checkAndSendUrls = async () => {
    const storedUrls = getStoredUrls();
    
    // Only send if we have enough URLs and we're not already loading
    if (storedUrls.length >= minUrls && !loading) {
      logger.info(`Found ${storedUrls.length} URLs to send to Airtable`);
      await sendStoredUrlsToAirtable();
    }
  };

  useEffect(() => {
    // Check immediately on mount
    checkAndSendUrls();
    
    // Set up interval to check periodically
    intervalRef.current = setInterval(checkAndSendUrls, checkInterval);
    
    // Clean up interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkInterval, minUrls, loading]);

  // This component doesn't render anything itself, just its children
  return <>{children}</>;
};

export default SendToAirtableProvider;
